import { ExpandMore } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { RootState, updatePageSettings } from "@platformx/authoring-state";
import { ShowToastSuccess, usePlatformAnalytics } from "@platformx/utilities";
import { XImageRender } from "@platformx/x-image-render";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import BackButton from "../BackButton/BackButton";
import { PageSocialShareInformation } from "../utils/editTypes";
import {
  socialShareDescriptionLength,
  socialShareNameLength,
  socialSharePreviewDescriptionLength,
  socialSharePreviewNameLength,
} from "../utils/prelemTypes";
import "./PageSettings.css";
import { useCustomStyle } from "./SEOBasics.style";
import TwitterSettings from "./TwitterSettings";

interface socialShareinsting {
  setPageId?: any;
}
const SocialShare = ({ setPageId }: socialShareinsting) => {
  const classes = useCustomStyle();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { page } = useSelector((state: RootState) => state);
  const pageInfo = { ...page?.pageSettings };
  const {
    SocialOgTitle,
    SocialOgDescription,
    SocialOgSiteName,
    SocialOgType,
    SocialOgURL,
    SocialOgLocale,
    SocialOgImage,
    PageName,
    PageDescription,
    PageURL,
  } = pageInfo;
  const data = {
    SocialOgTitle:
      SocialOgTitle !== undefined ? SocialOgTitle : PageName === undefined ? "" : PageName, //`${PageName} | ${siteLevelSchema.siteName}`,
    SocialOgDescription:
      SocialOgDescription !== undefined
        ? SocialOgDescription
        : PageDescription === undefined
          ? ""
          : PageDescription,
    SocialOgSiteName:
      SocialOgSiteName !== undefined && SocialOgSiteName !== ""
        ? SocialOgSiteName
        : page?.pageModel?.SiteName, //PageName === undefined ? "" : PageName, //`${PageName} | ${siteLevelSchema.siteName}`,
    SocialOgType: SocialOgType !== undefined ? SocialOgType : t("Website"),
    SocialOgURL:
      SocialOgURL !== undefined && SocialOgURL !== ""
        ? SocialOgURL
        : PageURL === undefined
          ? ""
          : PageURL,
    SocialOgLocale: SocialOgLocale !== undefined ? SocialOgLocale : t("en_US"),
    SocialOgImage: SocialOgImage !== undefined ? SocialOgImage : "",
  };
  const initialSocialShare = useRef<PageSocialShareInformation>(data);
  const [socialShareInfo, setSocialShareInfo] = useState<PageSocialShareInformation>(
    initialSocialShare.current,
  );
  const ogTypes = [t("website"), t("article")];
  const ogLocale = [t("page_en_us"), t("page_en_gb"), t("page_en_fr")];

  // Function to handle input field changes
  const handleDataChange = (event, fieldType) => {
    setSocialShareInfo({ ...socialShareInfo, [fieldType]: event.target.value });
  };
  const [handleImpression] = usePlatformAnalytics();
  const saveSocialShare = () => {
    dispatch(updatePageSettings({ pageInfo: socialShareInfo }));
    const pageDataObj = {
      eventType: "SocialShare PageSetting Saved",
      socialShareSaved: true,
    };
    handleImpression(pageDataObj.eventType, pageDataObj);
    ShowToastSuccess(`${t("social_share_info_toast")} ${t("saved_toast")}`);
  };

  const updateField = (d) => {
    setSocialShareInfo({ ...socialShareInfo, SocialOgImage: d?.original_image?.Thumbnail });
  };

  return (
    <Box className='pageSettingmainWp'>
      <BackButton setPageId={setPageId} Title={t("page_social_share")} />
      <Box
        className='rowBox x-image-render'
        //  sx={{ border: "1px solid #EFF0F6", borderRadius: "5px" }}
      >
        <Typography className='labelbox' variant='p4regular'>
          {t("page_seo_image")}
          <span style={{ position: "relative" }}>
            <Tooltip title='Image size should be optimal (<5mb).' arrow>
              <InfoIcon
                fontSize='small'
                style={{
                  marginLeft: "8px",
                  cursor: "pointer",
                  height: "18px",
                  width: "18px",
                  position: "absolute",
                  bottom: "0px",
                }}
              />
            </Tooltip>
          </span>
        </Typography>
        <XImageRender
          callBack={updateField}
          editData={{
            original_image: {
              original_image_relative_path:
                socialShareInfo.SocialOgImage?.split(".").length > 0
                  ? socialShareInfo.SocialOgImage.split(".")[0]
                  : "",
              ext:
                socialShareInfo.SocialOgImage?.split(".").length > 1
                  ? `.${socialShareInfo.SocialOgImage?.split(".")[1]}`
                  : "",
            },
          }}
          isCrop={false}
        />
        <Box
          className={`${classes.seoBasicsWrapper} seoBasics rowBox`}
          //  sx={{ padding: "5px 10px", margin: "0 !important" }}
        >
          <Typography className='labelbox' variant='p4regular'>
            {socialShareInfo.SocialOgURL ? socialShareInfo.SocialOgURL : ""}
          </Typography>
          <Typography className='labelbox' variant='p4regular' sx={{ overflowWrap: "break-word" }}>
            {socialShareInfo.SocialOgTitle.substring(0, socialSharePreviewNameLength)}
            {socialShareInfo.SocialOgTitle.length > socialSharePreviewNameLength && (
              <span>...</span>
            )}
          </Typography>
          <Typography className='labelbox' variant='p4regular' sx={{ overflowWrap: "break-word" }}>
            {socialShareInfo.SocialOgDescription.substring(0, socialSharePreviewDescriptionLength)}
            {socialShareInfo.SocialOgDescription.length > socialSharePreviewDescriptionLength && (
              <span>...</span>
            )}
          </Typography>
        </Box>
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_seo_title")}
          <span style={{ position: "relative" }}>
            <Tooltip
              title='Your title should be between 30-60 characters, with a maximum of 90.'
              arrow>
              <InfoIcon
                fontSize='small'
                style={{
                  marginLeft: "8px",
                  cursor: "pointer",
                  height: "18px",
                  width: "18px",
                  position: "absolute",
                  bottom: "0px",
                }}
              />
            </Tooltip>
          </span>
        </Typography>
        <TextField
          variant='filled'
          fullWidth
          multiline
          value={socialShareInfo.SocialOgTitle}
          onChange={(e) => handleDataChange(e, "SocialOgTitle")}
          size='small'
          placeholder={t("page_search_title_placeholder")}
          inputProps={{ maxLength: socialShareNameLength }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_seo_description")}
          <span style={{ position: "relative" }}>
            <Tooltip
              title='Your description should be between 55 and 200 characters long, with a maximum of 300.'
              arrow>
              <InfoIcon
                fontSize='small'
                style={{
                  marginLeft: "8px",
                  cursor: "pointer",
                  height: "18px",
                  width: "18px",
                  position: "absolute",
                  bottom: "0px",
                }}
              />
            </Tooltip>
          </span>
        </Typography>
        <TextField
          variant='filled'
          fullWidth
          multiline
          value={socialShareInfo.SocialOgDescription}
          onChange={(e) => handleDataChange(e, "SocialOgDescription")}
          size='small'
          placeholder={t("page_info_about_placeholder")}
          inputProps={{ maxLength: socialShareDescriptionLength }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_seo_url")}
        </Typography>
        <TextField
          variant='filled'
          fullWidth
          multiline
          value={socialShareInfo.SocialOgImage}
          onChange={(e) => handleDataChange(e, "SocialOgImage")}
          size='small'
          placeholder={t("page_seo_url_placeholder")}
          inputProps={{ readOnly: true }}
        />
      </Box>
      <Box className='rowBox'>
        <TwitterSettings />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_ogsite")}
        </Typography>
        <TextField
          variant='filled'
          fullWidth
          multiline
          value={socialShareInfo.SocialOgSiteName}
          onChange={(e) => handleDataChange(e, "SocialOgSiteName")}
          size='small'
          placeholder={t("page_info_about_placeholder")}
          inputProps={{ maxLength: socialShareNameLength }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_ogtype")}
        </Typography>
        <FormControl size='small' variant='filled' sx={{ width: "100%" }}>
          <Select
            labelId='demo-simple-select-standard-label'
            id='demo-simple-select-standard'
            value={socialShareInfo.SocialOgType}
            size='small'
            variant='filled'
            onChange={(e) => handleDataChange(e, "SocialOgType")}
            IconComponent={() => <ExpandMore />}>
            {ogTypes.map((item, index) => {
              return (
                <MenuItem value={item} key={index}>
                  {item}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_ogurl")}
        </Typography>
        <TextField
          variant='filled'
          fullWidth
          multiline
          value={socialShareInfo.SocialOgURL}
          onChange={(e) => handleDataChange(e, "SocialOgURL")}
          size='small'
          disabled
          placeholder={t("page_ogurl_placeholder")}
          inputProps={{ readOnly: true }}
        />
      </Box>
      <Box className='rowBox'>
        <Typography className='labelbox' variant='p4regular'>
          {t("page_oglocal")}
        </Typography>
        <FormControl size='small' variant='filled' sx={{ width: "100%" }}>
          <Select
            labelId='demo-simple-select-standard-label'
            id='demo-simple-select-standard'
            size='small'
            variant='filled'
            value={socialShareInfo.SocialOgLocale}
            onChange={(e) => handleDataChange(e, "SocialOgLocale")}
            IconComponent={() => <ExpandMore />}>
            {ogLocale.map((item, index) => {
              return (
                <MenuItem value={item} key={index}>
                  {item}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>
      <Box className='rowBox'>
        <Button
          variant='primaryButton'
          disabled={initialSocialShare.current === socialShareInfo}
          sx={{ width: "100%" }}
          onClick={saveSocialShare}
          data-testid='save-social-share'>
          {t("done")}
        </Button>
      </Box>
    </Box>
  );
};
export default SocialShare;
